import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Mail, MessageCircle, QrCode, Phone, FileText, CheckCircle2, ArrowRight, Globe } from "lucide-react";
import { useTranslation } from "@/i18n/translations";

function A({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: "-60px" });
  return <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={v ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>{children}</motion.div>;
}

export default function PayByLinkPage() {
  const { t: tr } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const CHANNELS = [
    { icon: Mail, title: tr("plink.email"), desc: tr("plink.emailD") },
    { icon: MessageCircle, title: tr("plink.sms"), desc: tr("plink.smsD") },
    { icon: Phone, title: tr("plink.call"), desc: tr("plink.callD") },
    { icon: QrCode, title: tr("plink.qr"), desc: tr("plink.qrD") },
    { icon: FileText, title: tr("plink.invoice"), desc: tr("plink.invoiceD") },
    { icon: Globe, title: tr("plink.social"), desc: tr("plink.socialD") },
  ];

  const STEPS = [
    { num: "1", title: tr("plink.s1"), desc: tr("plink.s1D") },
    { num: "2", title: tr("plink.s2"), desc: tr("plink.s2D") },
    { num: "3", title: tr("plink.s3"), desc: tr("plink.s3D") },
    { num: "4", title: tr("plink.s4"), desc: tr("plink.s4D") },
  ];

  const ADV_FEATURES = [
    tr("plink.advF1"), tr("plink.advF2"), tr("plink.advF3"), tr("plink.advF4"),
    tr("plink.advF5"), tr("plink.advF6"), tr("plink.advF7"),
  ];

  const WAYS = [
    { title: tr("plink.w1"), desc: tr("plink.w1D") },
    { title: tr("plink.w2"), desc: tr("plink.w2D") },
    { title: tr("plink.w3"), desc: tr("plink.w3D") },
  ];

  const USE_CASES = [
    { title: tr("plink.u1"), desc: tr("plink.u1D") },
    { title: tr("plink.u2"), desc: tr("plink.u2D") },
    { title: tr("plink.u3"), desc: tr("plink.u3D") },
  ];

  return (
    <div data-testid="pay-by-link-page">
      <SEO page="plink" />
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#0A1128]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <A>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("plink.tag")}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-4 leading-[1.1]">{tr("plink.title")}</h1>
              <p className="text-base md:text-lg text-slate-300 mt-6 leading-relaxed">{tr("plink.desc")}</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-7 py-3 text-sm hover:bg-[#00A844] transition-all">{tr("hero.cta1")} <ArrowRight size={16} /></Link>
                <Link to="/tarifs" className="inline-flex items-center justify-center font-semibold border-2 border-white/20 text-white rounded-full px-7 py-3 text-sm hover:bg-white/10 transition-all">{tr("cta.pricing")}</Link>
              </div>
            </A>
            <A delay={0.2}>
              <img src="https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL0VhSHdwaUhyeHZBWURTcTRrUzdWLmpwZyJ9:adyen:Sr6W8lsXMs26c5LELNQXimF9KrGydTpWfUj2Alngd_0?format=webp&width=800&height=450" alt="Pay by Link" loading="lazy" className="w-full rounded-2xl border border-white/10" />
            </A>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <A className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("plink.chanTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{tr("plink.chanTitle")}</h2>
            <p className="text-base text-[#475569] mt-4 max-w-lg mx-auto">{tr("plink.chanDesc")}</p>
          </A>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CHANNELS.map((c, i) => (
              <A key={i} delay={i * 0.08}>
                <div className="feature-card bg-[#F8F9FA] rounded-xl border border-slate-100 p-7 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#00C853]/10 flex items-center justify-center mb-4"><c.icon size={22} className="text-[#00C853]" /></div>
                  <h3 className="text-base font-semibold text-[#0A1128] mb-1.5">{c.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{c.desc}</p>
                </div>
              </A>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <A className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("plink.howTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{tr("plink.howTitle")}</h2>
          </A>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((s, i) => (
              <A key={i} delay={i * 0.12}>
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-[#00C853] flex items-center justify-center mb-4"><span className="text-lg font-bold text-white">{s.num}</span></div>
                  <h3 className="text-lg font-semibold text-[#0A1128]">{s.title}</h3>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.desc}</p>
                </div>
              </A>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <A>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("plink.advTag")}</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{tr("plink.advTitle")}</h2>
              <p className="text-base text-[#475569] mt-4 leading-relaxed">{tr("plink.advDesc")}</p>
              <ul className="mt-6 space-y-3">
                {ADV_FEATURES.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-[#00C853] mt-0.5 shrink-0" /><span className="text-sm text-[#475569]">{item}</span></li>
                ))}
              </ul>
            </A>
            <A delay={0.2}>
              <div className="bg-[#0A1128] rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-5">{tr("plink.ways")}</h3>
                {WAYS.map((o, i) => (
                  <div key={i} className="flex items-start gap-3 mt-4">
                    <div className="w-6 h-6 rounded bg-[#00C853]/10 flex items-center justify-center shrink-0 mt-0.5"><span className="text-xs font-bold text-[#00C853]">{i + 1}</span></div>
                    <div><div className="text-sm font-medium text-white">{o.title}</div><div className="text-xs text-slate-400">{o.desc}</div></div>
                  </div>
                ))}
              </div>
            </A>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#F8F9FA]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <A>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight">{tr("plink.idealTitle")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
              {USE_CASES.map((u, i) => (
                <A key={i} delay={i * 0.1}>
                  <div className="bg-white rounded-xl border border-slate-200 p-6 h-full">
                    <h3 className="text-base font-semibold text-[#0A1128] mb-2">{u.title}</h3>
                    <p className="text-sm text-[#475569]">{u.desc}</p>
                  </div>
                </A>
              ))}
            </div>
          </A>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#1B2B5B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <A>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">{tr("plink.ctaTitle")}</h2>
            <p className="text-base text-slate-300 mt-4 max-w-md mx-auto">{tr("plink.ctaDesc")}</p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 mt-8">
              <a href="https://pay.ayapay.ch/register" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-8 py-3.5 text-sm hover:bg-[#00A844] transition-all">{tr("appFeat.create")} <ArrowRight size={16} /></a>
              <Link to="/contact" className="inline-flex items-center justify-center font-semibold border-2 border-white/20 text-white rounded-full px-8 py-3.5 text-sm hover:bg-white/10 transition-all">{tr("hero.cta1")}</Link>
            </div>
          </A>
        </div>
      </section>
    </div>
  );
}
